/**
 * @mhmo91/ican — core types.
 *
 * Pure ABAC: every rule is `when(cond).allow/deny(...actions)` where
 * cond is a frozen AST built once at module load by a recording proxy.
 * The kernel knows nothing about tenants, roles, or backends.
 */
import type { z } from 'zod';

/**
 * Minimal universal user shape. Each entity's `User` generic must
 * extend this so `me.uid` is always defined.
 */
export type UserBase = {
  readonly uid: string;
};

/**
 * Anything a `FieldRef.eq(...)` can accept on its right-hand side as
 * a constant — the runtime evaluator compares with `===`. Lists use
 * `.hasAny/hasAll/hasOnly` over `readonly Literal[]`.
 */
export type Literal = string | number | boolean | null;

/**
 * A path into the runtime `(data, me)` pair. The first element selects
 * the source object; the rest are property keys. Frozen at AST build
 * time so the runtime evaluator never allocates on the hot path.
 *
 * `workspace` roots a path at the workspace document this entity's own
 * document lives under, which no plane resolves the same way: the emitter
 * reads it with a `get()`, and the kernel is handed one document and an
 * actor and never that ancestor (see `evaluate.ts`).
 */
export type FieldPath = readonly [
  source: 'data' | 'me' | 'pathVar' | 'workspace' | 'parent',
  ...keys: string[],
];

export type CmpOp = 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte';

/**
 * The condition AST. `Cond` is a closed sum — every node a runtime
 * evaluator and a Firestore-rules emitter can pattern-match on.
 *
 * Leaves carry a `FieldPath` (read at runtime) plus a constant or a
 * second `FieldPath`. Combinators (`and`/`or`/`not`) compose them.
 */
export type Cond =
  /** Presence of the whole input record, independent of its schema fields. */
  | { readonly kind: 'dataExists' }
  | { readonly kind: 'pathOwner'; readonly variable: string; readonly prefix: string }
  | { readonly kind: 'parentAccess'; readonly parent: string; readonly rules: ReadonlyArray<{ readonly effect: RuleEffect; readonly cond: Cond }> }
  | {
      readonly kind: 'cmp';
      readonly op: CmpOp;
      readonly left: FieldPath;
      readonly right: Literal | FieldPath;
    }
  | { readonly kind: 'in'; readonly left: FieldPath; readonly values: readonly Literal[] }
  | { readonly kind: 'hasAny'; readonly left: FieldPath; readonly values: readonly Literal[] }
  | { readonly kind: 'hasAll'; readonly left: FieldPath; readonly values: readonly Literal[] }
  | { readonly kind: 'hasOnly'; readonly left: FieldPath; readonly values: readonly Literal[] }
  | { readonly kind: 'matches'; readonly left: FieldPath; readonly regex: string }
  /** Own map-key presence, including a present null value; missing parents return false. */
  | { readonly kind: 'exists'; readonly left: FieldPath }
  /**
   * Array-membership check using a dynamic actor field reference.
   * Expresses "this document's array field contains the resolved actor
   * value" — e.g. `data.sharedWith.containsMember(me.uid)`.
   *
   * Unlike `hasAny(values)`, which accepts a `Literal[]` constant list
   * (frozen at AST-build time), `containsMember` stores the right-hand
   * side as a `FieldPath` so the actor's uid is resolved at evaluation
   * time. The Firestore emitter translates this to the `in` membership
   * operator: `request.auth.uid in resource.data.<field>`.
   */
  | { readonly kind: 'containsMember'; readonly left: FieldPath; readonly actorRef: FieldPath }
  /**
   * Permission check. The runtime evaluator reads `permsField` (typically
   * `['me', 'permissions']`) and asserts the resolved array contains the
   * permission token, its namespace wildcard, or the global wildcard.
   *
   * The Firestore emitter ignores `permsField` (Firestore rules cannot
   * read consumer-built actor fields directly) and instead emits a call
   * to a `hasPermission(uid, wsId, [...])` helper function that resolves the
   * caller's role via a grant doc lookup → that workspace's role catalog.
   *
   * `workspace` — when present, the permission is held in that literal
   * workspace rather than the one the entity's path or denormalized field
   * resolves. The emitter quotes it into the helper call, so it resolves on any
   * path and in any list; the evaluator answers it only for an actor whose
   * active workspace is that id.
   */
  | {
      readonly kind: 'hasPermission';
      readonly permsField: FieldPath;
      readonly permission: string;
      readonly workspace?: string;
      /**
       * `workspaceField` — the permission is held in the workspace this
       * document's own `<workspaceField>` names, rather than the one the path
       * resolves. The doc-keyed counterpart of `memberOf(field)`, and the form
       * a path-bound entity needs when the path's workspace is not the
       * document's: the emitter reads `request.resource.data.<field>` on a
       * create and `resource.data.<field>` otherwise, so the write stays bound
       * to one subtree while the authority is checked against the tenant the
       * row actually belongs to.
       */
      readonly workspaceField?: string | FieldPath;
      readonly eventField?: FieldPath;
      readonly scope?: {
        readonly roleField: string;
        readonly idField?: FieldPath;
        readonly idsField?: FieldPath;
        readonly keysField?: FieldPath;
        readonly mode?: 'all' | 'any';
      };
    }
  /**
   * Bare workspace-membership check. The runtime evaluator reads the actor's
   * `workspaces` map and asserts the workspace id (resolved from the path
   * variable supplied at authoring time) is present as a key with a non-null
   * value (i.e. the actor has a role in that workspace).
   *
   * The Firestore emitter emits an inline membership expression reading the
   * one grant document the workspace id names.
   *
   * `wsIdVar` is the path-variable name that carries the workspace id
   * (e.g. `'workspaceId'` from a path like `workspaces/{workspaceId}/…`).
   * The runtime evaluator resolves membership from the actor's `workspaces`
   * map: `actor.workspaces[workspaceId] != null` gives the roleId or null.
   *
   * `wsIdField` — when present, the workspace id comes from that field on the
   * document instead of from the path (`memberOf('organizationId')`). This is
   * the form a `list` rule needs when the listed collection's own wildcard is
   * what would otherwise carry the id: Firestore leaves that wildcard unbound
   * during list evaluation, while a document field is resolvable and a
   * matching `where(field, '==', id)` on the client query pins it to one
   * value.
   *
   * Runtime: reads the actor's `workspaces` field to resolve membership.
   * Emitter: emits the role-existence look-up from grant docs in Firestore.
   * Both: true iff the actor has a role (roleId != null) in the workspace.
   */
  | {
      readonly kind: 'isMember';
      readonly envelope?: boolean;
      readonly wsIdVar: string;
      readonly wsIdField?: string;
    }
  /**
   * "Own role" check — the self-scoped counterpart to `hasPermission`.
   * True iff the actor holds a grant, in the workspace identified by
   * `wsIdVar`, whose `roleId` equals the value resolved from `idField`
   * (typically `data.id`) — "is this the role currently assigned to me".
   *
   * Reuses `isMember`'s actor-side resolution (`actor.workspaces[actor[wsIdVar]]`)
   * but compares the resolved roleId against `idField` instead of asserting
   * it is non-null.
   *
   * The Firestore emitter inlines the same grant-doc lookup `isMember`
   * uses, adding an `== <idField>` comparison.
   *
   * NOT safe for `list` rules — `idField` resolves through `resource.data`
   * (e.g. `resource.data.id`), which Firestore leaves unbound during
   * list-rule evaluation. The Firestore emitter throws if this Cond
   * reaches a `list` rule; gate list access separately (e.g.
   * `hasPermission` / `member`).
   */
  | {
      readonly kind: 'ownRole';
      readonly wsIdVar: string;
      readonly idField: FieldPath;
    }
  | { readonly kind: 'and'; readonly nodes: readonly Cond[] }
  | { readonly kind: 'or'; readonly nodes: readonly Cond[] }
  | { readonly kind: 'not'; readonly node: Cond };

// ---------------------------------------------------------------
// Write patterns — declared by `can(verb).when(...).writes(pattern)`
// ---------------------------------------------------------------

/**
 * Server-evaluated tokens that can appear as values in `.update({...})`
 * and `.set({...})` payloads. The Firestore-rules emitter expands them
 * into `request.time` and `request.auth.uid` respectively; the FE
 * helpers (`@mhmo91/ican/firestore`'s write-pattern executor) substitute
 * `serverTimestamp()` and the caller's uid at the SDK call site.
 */
export type WriteToken = 'serverTime' | 'me.uid';

/**
 * Values permitted inside a write-pattern's payload — either a literal
 * (passed through as-is) or a `WriteToken` (server-evaluated).
 */
export type WriteValue = Literal | WriteToken;

/**
 * Frozen write-pattern declaration carried on a finalized `Rule`. The
 * Firestore-rules emitter walks this to emit a tightly-scoped guard:
 * the doc must transition from the optional `preState` shape to the
 * exact `values` shape, touching only the declared fields.
 *
 * `updateMask` is `update` minus the post-state values: it declares WHICH
 * fields a client update may change and leaves each one's value free. Use
 * it where the writable fields are self-owned data whose values no policy
 * constrains (a display name, a UI language) while the rest of the
 * document — identity, admission, consent — is server-written. `update`
 * cannot express that: its field mask is derived from the keys of a
 * value map, so every masked-in field is also pinned to one literal.
 * Its optional `values` (`.updateMask([...]).values({...})`) pins some masked fields —
 * a server clock beside free identity fields — and leaves the rest free.
 *
 * `set`'s optional `fields` closes a created document to the allowed key set.
 * Every allowed key is required unless `requiredFields` supplies a subset;
 * the remaining keys may be omitted. `values` independently pins post-state
 * values, including fields the create must carry for that equality to hold.
 * A create has no pre-state for `diff(resource.data)` to compare against.
 *
 * `changes` is the only NEGATIVE-polarity pattern, and the only one a DENY
 * rule takes: it matches an update that changes at least one of the listed
 * fields. `updateMask` cannot say this — `hasOnly` on a deny would fire on a
 * patch touching ONLY the listed fields and stand aside for one that touches
 * them alongside anything else, which is the opposite of a guard. Carried on
 * `when(cond).deny('update').writes(w => w.changes([...]))`, it reserves those
 * fields to the authority `cond` names ACROSS EVERY allow arm at once, because
 * the emitter composes `(allow₁ || … || allowₙ) && !(deny)` — a per-arm
 * `updateMask` would have to be repeated on every arm and re-derived on the
 * next one.
 *
 * `changes`'s optional `values` (`.changes([...]).values({...})`) is what lets a
 * deny read BOTH planes of an update. The `cond` reads the stored document and
 * `values` pins the one the patch produces, so a transition — not a state — is
 * what the refusal names: `when(d => d.paid.eq(true)).deny('update')
 * .writes(w => w.changes(['paidAt']).values({ paid: true }))` refuses a second
 * payment mark while leaving the un-mark that clears the first, which the
 * stored plane alone cannot tell apart. Unlike `updateMask`'s `values`, a
 * pinned field need not appear in the list: the list is the trigger (a key the
 * patch moved), the pins are the shape it landed on, and a guard routinely
 * keys on one field while pinning another.
 *
 * Write patterns are an EMITTER-SIDE concept. The runtime kernel never
 * reads them: `me.can(action, Entity, data)` is handed one proposed
 * end-state and cannot see which keys a patch changed, so a field mask is
 * unrepresentable there by construction. A rule carrying one is therefore
 * enforced at the Firestore seam only, and an equivalence fixture that
 * exercises the mask asserts the engine decision directly via
 * `expectEngine`. On a `changes` deny the kernel therefore reads the deny's
 * `cond` alone and fails CLOSED — `me.can('update', …)` answers false for
 * every actor the deny's condition matches, whichever fields the patch
 * actually carries — which is the safe direction for a UI predictor and the
 * reason a `changes` deny states its authority test in `cond`, never the
 * field list.
 */
export type WritePattern =
  | {
      readonly kind: 'update';
      readonly values: Readonly<Record<string, WriteValue>>;
      readonly preState?: Cond;
    }
  | {
      readonly kind: 'updateMask';
      readonly fields: ReadonlyArray<string>;
      readonly values?: Readonly<Record<string, WriteValue>>;
      readonly preState?: Cond;
    }
  | {
      readonly kind: 'set';
      readonly values: Readonly<Record<string, WriteValue>>;
      readonly fields?: ReadonlyArray<string>;
      readonly requiredFields?: ReadonlyArray<string>;
    }
  | {
      readonly kind: 'changes';
      readonly fields: ReadonlyArray<string>;
      readonly values?: Readonly<Record<string, WriteValue>>;
      readonly postState?: Cond;
    }
  | { readonly kind: 'delete'; readonly preState?: Cond };

// ---------------------------------------------------------------
// FieldRef typing — methods available per T
// ---------------------------------------------------------------

type BaseRef<T> = {
  exists(): Cond;
  eq(value: T | FieldRef<T>): Cond;
  ne(value: T | FieldRef<T>): Cond;
};

type OrderingRef<T> = [T] extends [number]
  ? {
      gt(value: T | FieldRef<T>): Cond;
      gte(value: T | FieldRef<T>): Cond;
      lt(value: T | FieldRef<T>): Cond;
      lte(value: T | FieldRef<T>): Cond;
    }
  : Record<never, never>;

type InRef<T> = [T] extends [string | number | boolean]
  ? { in(values: readonly T[]): Cond }
  : Record<never, never>;

type StringRef<T> = [T] extends [string] ? { matches(regex: string): Cond } : Record<never, never>;

type ListRef<T> = NonNullable<T> extends readonly (infer U)[]
  ? {
      hasAny(values: readonly U[]): Cond;
      hasAll(values: readonly U[]): Cond;
      hasOnly(values: readonly U[]): Cond;
      /**
       * True when the array contains the value resolved from an actor
       * field reference at evaluation time. Use for dynamic membership
       * checks such as `data.sharedWith.containsMember(me.uid)`.
       *
       * The `ref` argument must be a `FieldRef` produced by the recording
       * proxy (e.g. `me.uid`). Literals are rejected by the type: use
       * `.hasAny([literal])` for constant-list membership checks.
       *
       * Emitted Firestore form: `<actorExpr> in resource.data.<field>`
       * (e.g. `request.auth.uid in resource.data.sharedWith`).
       */
      containsMember(ref: FieldRef<U>): Cond;
    }
  : Record<never, never>;

/**
 * Typed reference into the entity schema or actor shape. Returned by
 * the recording proxy on every property access; method calls append
 * `Cond` nodes to the rule's AST.
 */
export type FieldRef<T> = BaseRef<T> & OrderingRef<T> & InRef<T> & StringRef<T> & ListRef<T>;

// ---------------------------------------------------------------
// DataProxy / UserProxy — typed views the rule callback receives
// ---------------------------------------------------------------

/**
 * Recursive mapping: each key on the schema (or actor) is a
 * `FieldRef<T[K]>` — comparable directly (`data.sell.eq(...)`) — and,
 * when `T[K]` is a plain object (not an array, stripped of
 * null/undefined), also carries its own nested keys as further
 * `ProxyOf`, so `data.sell.visibility` chains to a `FieldRef<string>`.
 * The runtime proxy (`proxy.ts`'s `makeFieldRef`) and the Firestore
 * emitter (`visitor.ts`'s `ref`) both walk arbitrary-depth `data.X`
 * paths; only `me.X` and `pathVar` stay flat (actor claims / path
 * variables Firestore rules resolve are single-segment by nature).
 */
type IsPlainObject<T> = T extends readonly unknown[] ? false : T extends object ? true : false;
type KnownKeys<T> = keyof {
  [K in keyof T as K extends string ? string extends K ? never : K : never]: unknown;
};

type ProxyOf<T> = {
  readonly [K in KnownKeys<T> & keyof T & string]-?: FieldRef<T[K]> &
    (IsPlainObject<NonNullable<T[K]>> extends true
      ? ProxyOf<NonNullable<T[K]>>
      : Record<never, never>);
};

export type DataProxy<S extends z.ZodTypeAny> = ProxyOf<z.infer<S>>;
export type UserProxy<U extends UserBase> = ProxyOf<U>;
/**
 * The recording proxy for the workspace document an entity's own document
 * lives under. Keyed on the workspace's inferred type rather than its zod
 * schema, since the entity being authored does not import that schema — only
 * the type — and the type is what narrows `keyof` for typo detection.
 */
export type WorkspaceProxy<W> = ProxyOf<W>;
export type ParentProxy<P> = ProxyOf<P>;
/** Real loaded ancestors, supplied by the consumer; absent ancestors fail closed. */
/** Loaded canonical parents and actual bound paths; request payloads never confer parent authority. */
export type AccessContext = {
  readonly parents?: Readonly<Record<string, unknown>>;
  readonly pathVars?: Readonly<Record<string, string>>;
};

// ---------------------------------------------------------------
// Rule + Entity
// ---------------------------------------------------------------

export type RuleEffect = 'allow' | 'deny';

/**
 * A rule attached to an entity. The third parameter `A` is the
 * *union* of action names this rule covers (typically narrowed to the
 * specific tuple the rule was authored against, e.g. `'view'` or
 * `'edit' | 'dispatch'`). The entity's `pipe(...)` accepts rules
 * whose `A` extends the entity's declared actions union — so a typo
 * in `.allow('disptch')` fails inside `pipe()`.
 *
 * `_actorBrand` and `_dataBrand` are phantom — they keep the rule
 * generic in `Actor` and `Data` so the consumer can't append a rule
 * built for a different entity.
 */
export type Rule<User extends UserBase, Data, A extends string = string> = {
  readonly effect: RuleEffect;
  readonly actions: ReadonlyArray<A>;
  readonly cond: Cond;
  readonly writePattern?: WritePattern;
  readonly handlerName?: string;
  readonly _userBrand?: (u: User) => void;
  readonly _dataBrand?: (d: Data) => void;
};

/**
 * Brand discriminator. Every entity carries this; the firestore CLI
 * scans the consumer's source tree, dynamic-imports each TS module,
 * and filters its exports by this brand to assemble the policy.
 */
export const ENTITY_BRAND: unique symbol = Symbol.for('@mhmo91/ican/entity');

/**
 * Adapter binding — opaque to the kernel. Each backend adapter
 * (Firestore, Postgres-RLS, Cedar, …) exports a factory that returns
 * one of these. The kernel preserves them on the entity in the
 * `_adapters` array; the relevant adapter filters to its own bindings
 * by the unique `_adapter` discriminator at emit time.
 */
export type AdapterBinding = { readonly _adapter: string };

/**
 * Final entity — has rules. `me.can(...)` reads rules off this object.
 * Adapters (Firestore) walk `rules` plus `_adapters` to emit rule files.
 */
export type Entity<
  User extends UserBase,
  Actions extends readonly string[],
  Schema extends z.ZodTypeAny,
> = {
  readonly [ENTITY_BRAND]: true;
  readonly schema: Schema;
  readonly actions: Actions;
  readonly namespace?: string;
  readonly rules: ReadonlyArray<Rule<User, z.infer<Schema>, Actions[number]>>;
  readonly _adapters: ReadonlyArray<AdapterBinding>;
  adaptor(binding: AdapterBinding): Entity<User, Actions, Schema>;
  readonly _schemaType?: z.infer<Schema>;
};

/**
 * `hasPermission`'s options. `workspace` names the workspace the permission is
 * held in as a literal id, in place of the one the entity's path resolves.
 */
/**
 * `workspace` names the workspace as a literal id. `workspaceField` names a
 * FIELD on the document that carries it — the form for a document whose path
 * is bound to one tenant while the document belongs to another, as a buyer's
 * negotiated price does hanging under the supplier's good. The two are
 * mutually exclusive: a leaf resolves its workspace from one source.
 */
export type HasPermissionOptions = {
  readonly workspace?: string;
  readonly workspaceField?: string | FieldRef<string | null | undefined>;
  readonly event?: FieldRef<string | null | undefined>;
  /** Missing/null role scope preserves an unrestricted role; [] grants no subjects. */
  readonly scope?: {
    readonly roleField: string;
    readonly mode?: 'all' | 'any';
  } & (
    | { readonly id: FieldRef<string | null | undefined>; readonly ids?: never; readonly keys?: never }
    | { readonly ids: FieldRef<readonly string[] | null | undefined>; readonly id?: never; readonly keys?: never }
    | { readonly keys: FieldRef<object | null | undefined>; readonly id?: never; readonly ids?: never }
  );
};

/**
 * Builder helpers handed to the `pipe(...)` callback. Each is typed
 * against the entity's actor + schema so `data.X` and `me.X` narrow
 * correctly inside the rule callbacks.
 */
export type EntityBuilder<
  User extends UserBase,
  Actions extends readonly string[],
  Schema extends z.ZodTypeAny,
> = {
  when(callback: (data: DataProxy<Schema>, me: UserProxy<User>) => Cond): {
    allow<As extends ReadonlyArray<Actions[number]>>(
      ...actions: As
    ): Rule<User, z.infer<Schema>, As[number]>;
    deny<As extends ReadonlyArray<Actions[number]>>(
      ...actions: As
    ): import('./when.ts').DenyChain<User, Schema, As[number]>;
  };
  can: <V extends Actions[number]>(
    verb: V,
  ) => {
    when(
      callback: (data: DataProxy<Schema>, me: UserProxy<User>) => Cond,
    ): import('./can.ts').WhenChain<User, Schema, V>;
  };
  and(...nodes: readonly Cond[]): Cond;
  or(...nodes: readonly Cond[]): Cond;
  not(node: Cond): Cond;
  /** False only for an absent record (null/undefined), never for a malformed existing record. */
  dataExists(): Cond;
  /**
   * Permission predicate, in the workspace the entity's path (or a
   * collection-group entity's denormalized `workspaceId`) resolves — or, with
   * `{ workspace }`, in that literal workspace on any path, or, with
   * `{ workspaceField }`, in the workspace the document's own field names.
   *
   * Reach for `{ workspaceField }` when the path is bound to one tenant and the
   * document belongs to another: a negotiated price hangs under the SUPPLIER's
   * good while the row is the BUYER's, so the path can bound where the write
   * lands without the bare form checking authority against the wrong tenant.
   */
  hasPermission(
    permsRef: FieldRef<readonly string[]>,
    permission: string,
    options?: HasPermissionOptions,
  ): Cond;
  /**
   * Bare workspace-membership predicate. Returns a `Cond` that is true when
   * the actor has a role in the workspace identified by the given path-variable
   * name (e.g. `member('workspaceId')` for an entity on the path
   * `workspaces/{workspaceId}/…`). A name the path does not bind, other than
   * the entity's default workspace variable, is refused at codegen.
   *
   * This is the membership half of `hasPermission` — role existence without
   * the `.permissions.hasAny(...)` clause. Use it to gate read/list access
   * where workspace membership is sufficient (per `business-permissions`
   * rule 3: MEMBERSHIP_GATES_VIEW_NO_VIEW_PERMISSION).
   *
   * Safe for `list` rules only when the path variable is a PARENT segment.
   * On an entity whose own collection wildcard carries the workspace id
   * (`workspaces/{workspaceId}` listed at the root), that wildcard is
   * unbound during list evaluation — use `memberOf` instead.
   */
  member(wsIdVar: string, options?: {readonly envelope?: boolean}): Cond;
  /** Exact authenticated uid ownership of a parent path variable, optionally namespaced. */
  pathOwner(variable: string, prefix?: string): Cond;
  /**
   * The workspace document this entity's own document lives under, as a
   * recording proxy: `workspace<Workspace>().published.eq(true)` gates a
   * child on a field of its parent gallery.
   *
   * The type argument is required and is not inferred — without it `keyof`
   * is empty and every field access fails to compile, which is the intended
   * forcing. Supply the workspace entity's own inferred type so a field
   * typo and a value-type mismatch are both compile errors.
   *
   * The emitted Firestore rule reads the parent with
   * `get(/databases/$(database)/documents/workspaces/$(<wsId>)).data.<field>`,
   * billed as one document read and cached within a request — so a list
   * query pays it once, not once per result. It is list-safe while the
   * workspace id comes from a PARENT path segment; on an entity whose own
   * collection wildcard carries that id, Firestore leaves it unbound during
   * list evaluation and the emitter refuses at codegen.
   *
   * The kernel cannot answer it. `me.can(...)` is handed one document and an
   * actor, never the ancestor, so the runtime evaluator declines to let this
   * leaf withhold a grant — the deployed rule is the only plane that
   * enforces it. Pair it with an arm the kernel CAN answer (membership,
   * a permission) so the affordance question stays honest.
   */
  workspace<W>(): WorkspaceProxy<W>;
  parent<P>(name: string): ParentProxy<P>;
  parentCan<E extends AnyEntity>(name: string, parent: E, action: ActionsOf<E>[number]): Cond;
  /**
   * Workspace-membership predicate keyed on a DOCUMENT FIELD rather than a
   * path variable: true when the actor holds a role in the workspace this
   * document's `<wsIdField>` names (e.g. `memberOf('organizationId')` on a
   * workspace doc that denormalizes its parent org id).
   *
   * This is the list-safe form. `member(wsIdVar)` resolves the workspace id
   * from the match path, which Firestore leaves unbound when the listed
   * collection's own wildcard carries it; a field on the document is always
   * resolvable, and a client query that constrains that same field with
   * `where(field, '==', id)` pins it to one value — so the grant lookup costs
   * one document access per QUERY, not one per result.
   *
   */
  memberOf(wsIdField: string): Cond;
  /**
   * "Own role" predicate — true when the actor's resolved roleId in the
   * workspace named by `wsIdVar` equals `idRef` (typically `data.id`). The
   * self-scoped counterpart to `hasPermission`: lets an actor read the ONE
   * role document currently assigned to them without holding the broader
   * manage permission that gates the rest of the role catalog.
   *
   * @example
   *   // Role doc at workspaces/{workspaceId}/roles/{id}
   *   can('read').when((data, me) =>
   *     or(hasPermission(me.permissions, 'workspaces.roles.manage'), ownRole('workspaceId', data.id)),
   *   )
   *
   * NOT safe for `list` rules — depends on the entity's own path variable,
   * unbound during list-rule evaluation. The Firestore emitter throws if
   * reached there; gate list access separately (`hasPermission` / `member`).
   */
  ownRole(wsIdVar: string, idRef: FieldRef<string>): Cond;
  /**
   * Reference a Firestore path variable by name (e.g. `userId` from a
   * path like `workspaces/{wsId}/memberships/{userId}`). The returned
   * `FieldRef` emits the variable name directly in Firestore rules.
   *
   * Safe for `get`, `update`, `delete`, `create` rules where the path
   * is fully bound. NOT safe for `list` on a wildcarded segment — at
   * list-evaluation time path wildcards are unbound (null), and the
   * comparison errors out, falling through to default-deny. For `list`,
   * use `data.<field>` and rely on the FE query's `where(...)` filter
   * matching the rule.
   *
   * Has no runtime value — `me.can(...)` with a rule that uses
   * `pathVar` will always evaluate to `false`.
   */
  pathVar(name: string, segment?: number): FieldRef<string>;
};

/**
 * Pre-`pipe` seed — the value returned by `entity<A>()({...})`.
 * `.pipe(builder)` invokes the builder once with typed
 * `{ when, and, or, not }`, runs each returned rule's callback against
 * recording proxies, and freezes the resulting AST onto the final
 * `Entity`.
 */
export type EntitySeed<
  User extends UserBase,
  Actions extends readonly string[],
  Schema extends z.ZodTypeAny,
> = {
  readonly schema: Schema;
  readonly actions: Actions;
  readonly namespace?: string;
  pipe(
    build: (
      ctx: EntityBuilder<User, Actions, Schema>,
    ) => ReadonlyArray<Rule<User, z.infer<Schema>, Actions[number]>>,
  ): Entity<User, Actions, Schema>;
};

/**
 * Top type for "any entity, regardless of actor / actions / schema."
 *
 * Defined structurally as the *minimum* shape every entity carries,
 * deliberately omitting the `adaptor(...)` method. Including the
 * method here would break covariance: `Entity<User>.adaptor()`
 * returns `Entity<User>`, which is not assignable to `Entity<never>`
 * recursively, so the typed entity wouldn't widen to `AnyEntity` at
 * collection / policy boundaries. Adapters that need binding
 * appendage know the concrete entity type at the seam where they
 * call `.adaptor(...)`; aggregators only need the read-side fields
 * below.
 */
export type AnyEntity = {
  readonly [ENTITY_BRAND]: true;
  readonly schema: z.ZodTypeAny;
  readonly actions: readonly string[];
  // `Rule<never, never, string>` rather than `Rule<never, unknown, string>`:
  // `_dataBrand?: (d: Data) => void` is contravariant in Data, and only
  // `Data = never` is assignable from every concrete `Rule<_, X, _>`.
  // `unknown` would tighten to top in the param position and break the
  // widening from the typed entity to AnyEntity.
  readonly rules: ReadonlyArray<Rule<never, never, string>>;
  readonly _adapters: ReadonlyArray<AdapterBinding>;
};

/**
 * Internal aggregate — a record of named entities. The firestore
 * adapter walks this shape from `collectBindings`. Library-internal:
 * consumers never construct one directly.
 */
export type Policy<P extends Record<string, AnyEntity> = Record<string, AnyEntity>> = {
  readonly [K in keyof P]: P[K];
};

// ---------------------------------------------------------------
// Type-level guards
// ---------------------------------------------------------------

/**
 * Branded compile-time error. Surfaces as the parameter type at the
 * failing call site so TS prints the templated message inline.
 */
export type TranspileError<Msg extends string> = {
  readonly _ican_error: true;
  readonly _msg: Msg;
};

/**
 * Actor-shape guard behind `entity`'s conditional return type
 * (`EntityFactoryOrReject`, in `entity.ts`). The guard is a structural
 * `keyof` test: an index-signed actor widens `keyof Actor` to `string`
 * (or `number`), so `string extends keyof User` (or
 * `number extends keyof User`) holds and the type resolves to the
 * branded {@link TranspileError}; a fixed-key actor's `keyof` is a
 * literal union of its declared keys, so neither `extends` check holds
 * and the type resolves to the actor itself.
 *
 * `entity<User>()`'s return type resolves this directly: when it
 * resolves to `TranspileError`, `entity<ActorWithIndexSignature>()`
 * itself still type-checks (there is no constraint to fail at the
 * declaration), but the returned value has no call signature, so
 * *invoking* it — `entity<ActorWithIndexSignature>()(seed)` — is what
 * tsc rejects, printing the message below inline. A fixed-key actor
 * maps to itself, so `entity<User>()` returns the real callable factory
 * and a normal actor still compiles and invokes as usual.
 *
 * The WHY is the kernel's typo-detection contract — an index signature
 * widens `keyof Actor` to `string`, silently accepting `me.<typo>` so a
 * banned actor keeps access because the typo'd deny never fires.
 */
export type RejectIndexSignature<User> = string extends keyof User
  ? TranspileError<'entity<Actor>(): actor may not carry a string index signature — it widens `keyof Actor` to `string` and silently accepts `me.<typo>`, defeating typo-based deny detection'>
  : number extends keyof User
    ? TranspileError<'entity<Actor>(): actor may not carry a numeric index signature — it widens `keyof Actor` and silently accepts `me.<typo>`, defeating typo-based deny detection'>
    : User;

/**
 * The bound actor — the value `createActor(data)` returns when `data`
 * is non-null and well-formed. Carries every field of the input plus
 * the bound `can(action, Entity, data)` method that walks the
 * entity's rules with deny-overrides + default-deny.
 *
 * `can` is constrained to `AnyEntity` rather than a fixed entity set:
 * `me.can` reads rules off the entity argument itself, so it accepts
 * any entity in scope; the action type narrows against that entity's
 * own `actions` tuple, preserving per-entity typo detection.
 */
export type Me<User extends UserBase> = User & {
  can<E extends AnyEntity>(
    action: ActionsOf<E>[number],
    entity: E,
    data: z.infer<E['schema']>,
    context?: AccessContext,
  ): boolean;
  hasPermission(permission: string): boolean;
};

/**
 * `createActor`'s parameter type. The reserved `can` field is
 * forbidden — it would shadow the bound method on the returned `Me`.
 * A `Custom` carrying `can` collapses to `TranspileError`, surfacing
 * the message inline at the call site.
 */
export type UserInput<User extends UserBase> = 'can' extends keyof User
  ? TranspileError<'User data may not declare a `can` field — it collides with the bound method'>
  : 'hasPermission' extends keyof User
    ? TranspileError<'User data may not declare a `hasPermission` field — it collides with the bound method'>
    : User;

export type ActionsOf<E> = E extends { readonly actions: infer As }
  ? As extends readonly string[]
    ? As
    : never
  : never;
